/**
 * Unified Token Estimation Service v2.0
 * 
 * SINGLE SOURCE OF TRUTH for all token estimation across the application.
 * Integrates with the centralized token consumption system for consistency.
 * 
 * This service provides:
 * - Fair token estimation aligned with actual Gemini API costs
 * - Configurable rates via admin dashboard
 * - Consistent estimation across chat, quiz, exam, and attachments
 * - Pre-emptive cost warnings
 * - Usage tracking and analytics
 * - Response-length-based post-calculation
 * 
 * Gemini API Token Reference (Flash model):
 * - 1 token ≈ 4 characters of text
 * - Input: ~$0.075 per 1M tokens
 * - Output: ~$0.30 per 1M tokens (4x input cost)
 * - Images: ~258 tokens base + variable
 * 
 * Our "token" system is symbolic (not 1:1 with API tokens) but proportional.
 * We charge ~1 Luca token per 100 API tokens for simplicity.
 */

import { Attachment, GlobalConfig, User } from '../types';
import { hasUnlimitedTokens } from '../utils/promoEffectsManager';

// ==================== Configuration Interface ====================

export interface TokenEstimationConfig {
    // Base rates (our symbolic tokens per operation)
    baseRates: {
        chatMessage: number;           // Simple chat message
        chatWithAttachment: number;    // Chat with file analysis
        quizQuestion: number;          // Per quiz question generated
        examQuestion: number;          // Per exam question (more complex)
        flashcard: number;             // Per flashcard generated
        summary: number;               // Document/session summary
    };
    
    // Complexity multipliers
    complexityMultipliers: {
        simple: number;      // Yes/no, definitions (0.5x - 1x)
        moderate: number;    // Standard explanations (1x - 1.5x)
        complex: number;     // Multi-step, calculations (1.5x - 2x)
        advanced: number;    // Professional topics, comprehensive (2x - 3x)
    };
    
    // Response length multipliers (based on expected output)
    lengthMultipliers: {
        short: number;       // < 500 chars
        medium: number;      // 500-2000 chars
        long: number;        // 2000-5000 chars
        veryLong: number;    // 5000-10000 chars
        comprehensive: number; // 10000+ chars
    };
    
    // Attachment costs
    attachmentCosts: {
        imageBase: number;         // Base cost for image analysis
        imagePerKB: number;        // Additional per KB
        pdfBase: number;           // Base cost for PDF
        pdfPerPage: number;        // Per page after first
        pptBase: number;           // PowerPoint base
        pptPerSlide: number;       // Per slide estimate
        textFileBase: number;      // Text/code files
        spreadsheetBase: number;   // Excel/CSV
    };
    
    // Feature costs
    featureCosts: {
        codeGeneration: number;    // Code blocks
        tableGeneration: number;   // Tables/charts
        mathComputation: number;   // Mathematical solutions
        journalEntry: number;      // Accounting entries
        chartVisualization: number; // Mermaid/chart generation
        quizMode: number;          // Interactive quiz
    };
    
    // Caps and limits
    limits: {
        maxPerResponse: number;      // Maximum tokens per single response
        maxPerQuiz: number;          // Maximum per quiz generation
        maxPerExam: number;          // Maximum per exam generation
        warningThreshold: number;    // Show warning above this
        confirmationThreshold: number; // Require confirmation above this
    };
    
    // Premium discounts (percentage)
    premiumDiscounts: {
        standard: number;    // 0% for standard
        premium: number;     // e.g., 20%
        max: number;         // e.g., 30%
    };
    
    // AI Persona multipliers (extra charges for different modes)
    personaMultipliers: {
        standard: number;     // Base mode (0.8x)
        educator: number;     // Adaptive teaching (1.0x - free, default mode)
        lawyer: number;       // Legal expert mode (1.3x)
        tutor?: number;       // Alias for educator
        professional?: number; // Alias for lawyer
        [key: string]: number | undefined;
    };
    
    // AI Depth multipliers (extra charges for detailed responses)
    depthMultipliers: {
        concise: number;       // Short responses (0.4x - max discount)
        standard: number;      // Standard (1.0x)
        comprehensive: number; // Deep learning (2.0x)
        expert: number;        // MAX-only professional (3.0x)
        balanced?: number;     // Alias for standard
        detailed?: number;     // Alias for comprehensive
        [key: string]: number | undefined;
    };
}

// ==================== Default Configuration ====================
// These values are designed to be FAIR and roughly proportional to actual API costs
// while keeping the user experience smooth and affordable

export const DEFAULT_TOKEN_ESTIMATION_CONFIG: TokenEstimationConfig = {
    // ==================== BALANCED TOKEN ECONOMY v5.0 ====================
    // ALIGNED WITH BUSINESS_MONETIZATION_PLAN.md
    // 
    // DESIGN PHILOSOPHY:
    // - FAIR to users: Charges should match value delivered
    // - PROFITABLE: Healthy margins while being competitive
    // - TRANSPARENT: Users understand what they're paying for
    // 
    // TARGET METRICS (from Business Plan):
    // - Free tier: 5,000 tokens/day = ~100 standard messages (experience quality, trigger purchase)
    // - Premium: 50,000 tokens/day = ~1,000 standard messages (solid daily limit)
    // - MAX: 200,000 tokens/day = ~4,000 standard messages (power users)
    //
    // QUALITY TIERS (visible to users):
    // - ⚡ Quick Answer: ~18-25 tokens (definitions, yes/no)
    // - 📝 Standard: ~50 tokens (explanations with examples)
    // - 🔬 Detailed Analysis: ~75-100 tokens (step-by-step with tables)
    // - 👔 Expert Consultation: ~150-200 tokens (full citations, professional recommendations)
    //
    // PROFIT ANALYSIS:
    // - API cost per message: ~₱0.03-0.08 (varies by length)
    // - Revenue per 50 tokens: ~₱0.25 (at ₱0.005/token)
    // - Profit margin: ~60-75% (fair & sustainable)
    
    baseRates: {
        // Base costs adjusted for ~100 standard messages on free tier
        chatMessage: 35,          // Base chat → 5000/35 = ~143 quick messages
        chatWithAttachment: 60,   // With files (~83 free analyses/day)
        quizQuestion: 10,         // Per quiz question generated
        examQuestion: 15,         // Per exam question (more complex)
        flashcard: 6,             // Simple flashcard generation
        summary: 45,              // Document/session summarization
    },
    
    // BALANCED: Proportional to value delivered
    complexityMultipliers: {
        simple: 0.5,       // Quick answers (~18 tokens) - definitions, yes/no ⚡
        moderate: 1.0,     // Standard (~35 tokens) - basic explanations 📝
        complex: 1.8,      // Detailed (~63 tokens) - multi-step, calculations 🔬
        advanced: 2.8,     // Expert (~98 tokens) - professional analysis 👔
    },
    
    // BALANCED: Match expected response lengths
    lengthMultipliers: {
        short: 0.6,        // Quick answers (~21 tokens) ⚡
        medium: 1.0,       // Standard responses (~35 tokens) 📝
        long: 1.6,         // Detailed explanations (~56 tokens) 🔬
        veryLong: 2.2,     // Comprehensive guides (~77 tokens) 👔
        comprehensive: 3.0, // Expert consultation (~105 tokens) 🌟
    },
    
    attachmentCosts: {
        // File analysis - substantial but fair for actual processing needed
        imageBase: 35,          // Image analysis base
        imagePerKB: 0.02,       // Per KB of image
        pdfBase: 45,            // PDF base cost
        pdfPerPage: 10,         // Per page of PDF
        pptBase: 65,            // PowerPoint base
        pptPerSlide: 12,        // Per slide
        textFileBase: 20,       // Text/code files
        spreadsheetBase: 40,    // Excel/CSV analysis
    },
    
    // BALANCED: Feature costs reflect actual value
    featureCosts: {
        codeGeneration: 35,     // Code block generation
        tableGeneration: 25,    // Table creation
        mathComputation: 30,    // Mathematical solutions
        journalEntry: 35,       // Accounting journal entries
        chartVisualization: 50, // Mermaid/chart generation
        quizMode: 15,           // Per interactive quiz question
    },
    
    limits: {
        maxPerResponse: 250,      // Cap single responses (prevents runaway costs)
        maxPerQuiz: 400,          // Quiz generation cap
        maxPerExam: 600,          // Exam generation cap
        warningThreshold: 150,    // Show warning above this
        confirmationThreshold: 250, // Require confirmation above this
    },
    
    // NO TIER DISCOUNTS - All users pay same rate per token
    // Premium/MAX users get MORE TOKENS, not cheaper rates
    // This is FAIR - same value for same money
    premiumDiscounts: {
        standard: 0,   // No discount
        premium: 0,    // No discount
        max: 0,        // No discount
    },
    
    // AI Persona multipliers - Expert mode costs more (justified by quality)
    // BALANCED: Premium is significant but not excessive
    personaMultipliers: {
        standard: 0.8,      // Professional assistant - slight discount
        educator: 1.0,      // Adaptive teaching - baseline (encourage learning)
        lawyer: 1.3,        // Legal expert - 30% premium (specialized legal analysis)
    },
    
    // AI Depth multipliers - Match new depth system v2.0
    depthMultipliers: {
        concise: 0.4,       // 60% discount for lightning-fast answers
        standard: 1.0,      // Default - baseline (was 'balanced')
        comprehensive: 2.0, // 100% premium for deep learning
        expert: 3.0,        // 200% premium for professional documentation (MAX only)
    },
};

// ==================== Types ====================

export type ComplexityLevel = 'simple' | 'moderate' | 'complex' | 'advanced';
export type ResponseLength = 'short' | 'medium' | 'long' | 'veryLong' | 'comprehensive';

export interface EstimationInput {
    // Message content
    message: string;
    
    // Attachments
    attachments?: Attachment[];
    attachmentSizeKB?: number;
    
    // Context
    historyLength?: number;
    
    // User settings
    aiDepth?: 'concise' | 'balanced' | 'detailed' | 'comprehensive';
    aiMode?: 'standard' | 'tutor' | 'professional';
    
    // Operation type
    operationType?: 'chat' | 'quiz' | 'exam' | 'summary' | 'flashcard';
    
    // For quiz/exam
    questionCount?: number;
    difficulty?: string;
}

export interface EstimationResult {
    // Token breakdown
    baseTokens: number;
    complexityTokens: number;
    lengthTokens: number;
    attachmentTokens: number;
    featureTokens: number;
    discountTokens: number;
    
    // Final values
    subtotal: number;
    total: number;
    capped: boolean;
    originalTotal: number;
    
    // Analysis
    complexity: ComplexityLevel;
    expectedLength: ResponseLength;
    
    // Warnings
    exceedsWarning: boolean;
    exceedsConfirmation: boolean;
    warnings: string[];
    
    // For display
    breakdown: { label: string; value: number; }[];
}

export interface UserContext {
    tier: 'standard' | 'premium' | 'max';
    hasUnlimited: boolean;
    currentUsage: number;
    dailyLimit: number;
}

// ==================== Singleton Service ====================

class TokenEstimationService {
    private config: TokenEstimationConfig;
    private listeners: Set<(config: TokenEstimationConfig) => void> = new Set();
    
    constructor() {
        this.config = this.loadConfig();
    }
    
    // ==================== Configuration ====================
    
    private loadConfig(): TokenEstimationConfig {
        try {
            const stored = localStorage.getItem('luca_token_estimation_config');
            if (stored) {
                const parsed = JSON.parse(stored);
                return this.mergeWithDefaults(parsed);
            }
        } catch (error) {
            console.error('[TokenEstimation] Failed to load config:', error);
        }
        return { ...DEFAULT_TOKEN_ESTIMATION_CONFIG };
    }
    
    private mergeWithDefaults(partial: Partial<TokenEstimationConfig>): TokenEstimationConfig {
        return {
            baseRates: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.baseRates, ...(partial.baseRates || {}) },
            complexityMultipliers: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.complexityMultipliers, ...(partial.complexityMultipliers || {}) },
            lengthMultipliers: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.lengthMultipliers, ...(partial.lengthMultipliers || {}) },
            attachmentCosts: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.attachmentCosts, ...(partial.attachmentCosts || {}) },
            featureCosts: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.featureCosts, ...(partial.featureCosts || {}) },
            limits: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.limits, ...(partial.limits || {}) },
            premiumDiscounts: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.premiumDiscounts, ...(partial.premiumDiscounts || {}) },
            personaMultipliers: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.personaMultipliers, ...(partial.personaMultipliers || {}) },
            depthMultipliers: { ...DEFAULT_TOKEN_ESTIMATION_CONFIG.depthMultipliers, ...(partial.depthMultipliers || {}) },
        };
    }
    
    getConfig(): TokenEstimationConfig {
        return { ...this.config };
    }
    
    updateConfig(updates: Partial<TokenEstimationConfig>): void {
        this.config = this.mergeWithDefaults(updates);
        localStorage.setItem('luca_token_estimation_config', JSON.stringify(this.config));
        console.log('[TokenEstimation] Config updated');
        this.listeners.forEach(listener => listener(this.config));
    }
    
    resetToDefaults(): void {
        this.config = { ...DEFAULT_TOKEN_ESTIMATION_CONFIG };
        localStorage.removeItem('luca_token_estimation_config');
        console.log('[TokenEstimation] Config reset to defaults');
        this.listeners.forEach(listener => listener(this.config));
    }
    
    onConfigChange(callback: (config: TokenEstimationConfig) => void): () => void {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }
    
    // ==================== Complexity Detection ====================
    
    detectComplexity(message: string, hasAttachments: boolean): ComplexityLevel {
        const lower = message.toLowerCase();
        const wordCount = message.split(/\s+/).length;
        let score = 0;
        
        // ===== SIMPLE PATTERNS (reduce score) =====
        const simplePatterns = [
            /^(what is|define|who is|when|where|is it|yes or no|true or false)\b/i,
            /^(hi|hello|hey|thanks|thank you|ok|okay)\b/i,
        ];
        if (simplePatterns.some(p => p.test(message))) score -= 2;
        if (wordCount < 5) score -= 1;
        
        // ===== MODERATE PATTERNS (+1-2 each) =====
        const moderatePatterns = [
            /explain|describe|how does|what are|tell me about/i,
            /difference|similar|relate/i,
            /example|illustrate|demonstrate/i,
        ];
        score += moderatePatterns.filter(p => p.test(lower)).length * 1;
        if (wordCount >= 10 && wordCount < 20) score += 1;
        
        // ===== COMPLEX PATTERNS (+2 each) =====
        const complexPatterns = [
            /calculat|compute|solve|determine|analyze|compare/i,
            /step.*by.*step|multiple|both.*and/i,
            /journal entry|depreciation|amortization|accrual/i,
            /pro.*and.*con|advantage|disadvantage/i,
            /table|list.*all|enumerate|summarize/i,
            /how.*to|procedure|process|method/i,
        ];
        score += complexPatterns.filter(p => p.test(lower)).length * 2;
        
        // ===== ADVANCED PATTERNS (+3 each) =====
        const advancedPatterns = [
            /pfrs|pas\s*\d+|ifrs|gaap|accounting.*standard/i,
            /consolidat|deferred.*tax|business.*combination|merger/i,
            /comprehensive|thorough|in.*depth|detailed|complete/i,
            /vat|value.*added.*tax|income.*tax|taxation|withholding/i,
            /all.*about|everything.*about|complete.*guide|full.*discussion/i,
            /audit.*procedure|internal.*control|risk.*assessment/i,
            /financial.*statement.*analysis|ratio.*analysis/i,
            /cost.*accounting|variance.*analysis|budgeting/i,
            /further|more|elaborate|expand|deeper/i,
        ];
        score += advancedPatterns.filter(p => p.test(lower)).length * 3;
        
        // ===== ADDITIONAL FACTORS =====
        // Multiple questions increase complexity
        const questionCount = (message.match(/\?/g) || []).length;
        if (questionCount > 1) score += questionCount * 1;
        
        // Long messages suggest complex topics
        if (wordCount >= 20) score += 2;
        if (wordCount >= 40) score += 2;
        
        // Attachments add complexity
        if (hasAttachments) score += 3;
        
        // ===== DETERMINE LEVEL =====
        if (score < 2) return 'simple';
        if (score < 5) return 'moderate';
        if (score < 10) return 'complex';
        return 'advanced';
    }
    
    // ==================== Response Length Prediction ====================
    
    predictResponseLength(message: string, complexity: ComplexityLevel, aiDepth?: string): ResponseLength {
        const lower = message.toLowerCase();
        const wordCount = message.split(/\s+/).length;
        
        // Start with complexity-based default
        let baseLength: ResponseLength = 'medium';
        if (complexity === 'simple') baseLength = 'short';
        if (complexity === 'advanced') baseLength = 'long';
        
        // Check for length-indicating patterns
        if (/^(yes|no|what is|define|who is)\b/i.test(message) && wordCount < 10) {
            baseLength = 'short';
        }
        
        if (/list|enumerate|compare|all|everything|comprehensive/i.test(lower)) {
            baseLength = baseLength === 'short' ? 'medium' : 'long';
        }
        
        if (/table|matrix|complete.*list|all.*types|all.*requirements/i.test(lower)) {
            baseLength = 'veryLong';
        }
        
        if (/complete.*guide|thorough.*explanation|in.*depth.*discussion/i.test(lower) || wordCount > 50) {
            baseLength = 'comprehensive';
        }
        
        // Adjust for AI depth setting
        if (aiDepth === 'concise') {
            if (baseLength === 'comprehensive') baseLength = 'veryLong';
            else if (baseLength === 'veryLong') baseLength = 'long';
            else if (baseLength === 'long') baseLength = 'medium';
        } else if (aiDepth === 'detailed' || aiDepth === 'comprehensive') {
            if (baseLength === 'short') baseLength = 'medium';
            else if (baseLength === 'medium') baseLength = 'long';
        }
        
        return baseLength;
    }
    
    // ==================== Feature Detection ====================
    
    detectFeatures(message: string): string[] {
        const lower = message.toLowerCase();
        const features: string[] = [];
        
        if (/code|program|function|algorithm|script/i.test(lower)) features.push('code');
        if (/table|matrix|chart|graph|diagram/i.test(lower)) features.push('table');
        if (/calculat|compute|formula|equation|\d+\s*[+\-×÷*/]/i.test(lower)) features.push('math');
        if (/journal entry|debit|credit|ledger/i.test(lower)) features.push('journal');
        if (/visualiz|flowchart|mermaid|diagram/i.test(lower)) features.push('chart');
        if (/quiz|test|question.*me|practice/i.test(lower)) features.push('quiz');
        
        return features;
    }
    
    // ==================== Main Estimation ====================
    
    estimate(input: EstimationInput, userContext?: UserContext): EstimationResult {
        const { message, attachments = [], operationType = 'chat' } = input;
        
        // Detect complexity and length
        const hasAttachments = attachments.length > 0;
        const complexity = this.detectComplexity(message, hasAttachments);
        const expectedLength = this.predictResponseLength(message, complexity, input.aiDepth);
        const features = this.detectFeatures(message);
        
        // Calculate base tokens
        let baseTokens = 0;
        switch (operationType) {
            case 'quiz':
                baseTokens = this.config.baseRates.quizQuestion * (input.questionCount || 10);
                break;
            case 'exam':
                baseTokens = this.config.baseRates.examQuestion * (input.questionCount || 20);
                break;
            case 'summary':
                baseTokens = this.config.baseRates.summary;
                break;
            case 'flashcard':
                baseTokens = this.config.baseRates.flashcard * (input.questionCount || 10);
                break;
            default:
                baseTokens = hasAttachments 
                    ? this.config.baseRates.chatWithAttachment 
                    : this.config.baseRates.chatMessage;
        }
        
        // Apply complexity multiplier
        const complexityMultiplier = this.config.complexityMultipliers[complexity];
        const complexityTokens = Math.round(baseTokens * (complexityMultiplier - 1));
        
        // Apply length multiplier
        const lengthMultiplier = this.config.lengthMultipliers[expectedLength];
        const lengthTokens = Math.round(baseTokens * (lengthMultiplier - 1));
        
        // Calculate attachment costs
        let attachmentTokens = 0;
        for (const att of attachments) {
            const sizeKB = att.data ? att.data.length / 1024 : 0;
            
            if (att.mimeType?.startsWith('image/')) {
                attachmentTokens += this.config.attachmentCosts.imageBase;
                attachmentTokens += Math.round(sizeKB * this.config.attachmentCosts.imagePerKB);
            } else if (att.mimeType === 'application/pdf') {
                attachmentTokens += this.config.attachmentCosts.pdfBase;
                // Estimate pages (rough: 50KB per page)
                const estimatedPages = Math.max(1, Math.ceil(sizeKB / 50));
                attachmentTokens += (estimatedPages - 1) * this.config.attachmentCosts.pdfPerPage;
            } else if (att.mimeType?.includes('presentation') || att.mimeType?.includes('powerpoint')) {
                attachmentTokens += this.config.attachmentCosts.pptBase;
                // Estimate slides (rough: 100KB per slide)
                const estimatedSlides = Math.max(1, Math.ceil(sizeKB / 100));
                attachmentTokens += estimatedSlides * this.config.attachmentCosts.pptPerSlide;
            } else if (att.mimeType?.includes('spreadsheet') || att.mimeType?.includes('excel')) {
                attachmentTokens += this.config.attachmentCosts.spreadsheetBase;
            } else {
                attachmentTokens += this.config.attachmentCosts.textFileBase;
            }
        }
        
        // Calculate feature costs
        let featureTokens = 0;
        if (features.includes('code')) featureTokens += this.config.featureCosts.codeGeneration;
        if (features.includes('table')) featureTokens += this.config.featureCosts.tableGeneration;
        if (features.includes('math')) featureTokens += this.config.featureCosts.mathComputation;
        if (features.includes('journal')) featureTokens += this.config.featureCosts.journalEntry;
        if (features.includes('chart')) featureTokens += this.config.featureCosts.chartVisualization;
        if (features.includes('quiz')) featureTokens += this.config.featureCosts.quizMode;
        
        // Calculate subtotal
        const subtotal = baseTokens + complexityTokens + lengthTokens + attachmentTokens + featureTokens;
        
        // Apply premium discount
        let discountTokens = 0;
        let discountPercent = 0;
        if (userContext) {
            discountPercent = this.config.premiumDiscounts[userContext.tier] || 0;
            discountTokens = Math.round(subtotal * (discountPercent / 100));
        }
        
        // Calculate total
        let total = subtotal - discountTokens;
        let originalTotal = total;
        let capped = false;
        
        // Apply caps based on operation type
        let maxAllowed = this.config.limits.maxPerResponse;
        if (operationType === 'quiz') maxAllowed = this.config.limits.maxPerQuiz;
        if (operationType === 'exam') maxAllowed = this.config.limits.maxPerExam;
        
        if (total > maxAllowed) {
            originalTotal = total;
            total = maxAllowed;
            capped = true;
        }
        
        // Check for unlimited users
        if (userContext?.hasUnlimited) {
            total = 0;
            discountTokens = subtotal;
        }
        
        // Generate warnings
        const warnings: string[] = [];
        if (capped) {
            warnings.push(`Token cost capped from ${originalTotal} to ${maxAllowed}`);
        }
        if (total > this.config.limits.warningThreshold && !userContext?.hasUnlimited) {
            warnings.push(`This request may use ${total} tokens`);
        }
        if (userContext && (userContext.currentUsage + total) > userContext.dailyLimit && !userContext.hasUnlimited) {
            warnings.push(`This may exceed your daily limit`);
        }
        
        // Build breakdown for display
        const breakdown: { label: string; value: number }[] = [
            { label: 'Base cost', value: baseTokens },
        ];
        if (complexityTokens !== 0) {
            breakdown.push({ label: `Complexity (${complexity})`, value: complexityTokens });
        }
        if (lengthTokens !== 0) {
            breakdown.push({ label: `Response length (${expectedLength})`, value: lengthTokens });
        }
        if (attachmentTokens > 0) {
            breakdown.push({ label: `Attachments (${attachments.length})`, value: attachmentTokens });
        }
        if (featureTokens > 0) {
            breakdown.push({ label: `Features (${features.join(', ')})`, value: featureTokens });
        }
        if (discountTokens > 0) {
            breakdown.push({ label: `Discount (${discountPercent}%)`, value: -discountTokens });
        }
        if (capped) {
            breakdown.push({ label: 'Cap applied', value: total - originalTotal });
        }
        
        return {
            baseTokens,
            complexityTokens,
            lengthTokens,
            attachmentTokens,
            featureTokens,
            discountTokens,
            subtotal,
            total,
            capped,
            originalTotal,
            complexity,
            expectedLength,
            exceedsWarning: total > this.config.limits.warningThreshold,
            exceedsConfirmation: total > this.config.limits.confirmationThreshold,
            warnings,
            breakdown,
        };
    }
    
    // ==================== Convenience Methods ====================
    
    /**
     * Quick estimate for chat messages
     */
    estimateChat(message: string, attachments: Attachment[] = [], user?: User): EstimationResult {
        const userContext = user ? this.getUserContext(user) : undefined;
        return this.estimate({ message, attachments, operationType: 'chat' }, userContext);
    }
    
    /**
     * Estimate for quiz generation
     */
    estimateQuiz(questionCount: number, difficulty: string, user?: User): EstimationResult {
        const complexityBoost = difficulty === 'hard' || difficulty === 'very_hard' ? 1.5 : 1;
        const userContext = user ? this.getUserContext(user) : undefined;
        return this.estimate({
            message: `Generate ${questionCount} ${difficulty} quiz questions`,
            operationType: 'quiz',
            questionCount: Math.ceil(questionCount * complexityBoost),
        }, userContext);
    }
    
    /**
     * Calculate ACTUAL tokens based on response length (POST-response calculation)
     * This is the FAIR method - charges based on what the AI actually generated
     * Includes persona and depth multipliers
     */
    calculateFromResponse(
        inputChars: number,
        outputChars: number,
        complexity: ComplexityLevel,
        attachmentCount: number,
        user?: User,
        options?: {
            persona?: 'standard' | 'tutor' | 'professional';
            depth?: 'concise' | 'balanced' | 'detailed' | 'comprehensive';
        }
    ): { tokens: number; breakdown: { label: string; value: number }[] } {
        // Token calculation: ~4 characters per token (industry standard)
        const inputTokens = Math.ceil(inputChars / 4);
        const outputTokens = Math.ceil(outputChars / 4);
        
        // Fair pricing: charge based on actual API usage
        // Output tokens cost more (Gemini charges 4x for output vs input)
        // We simplify: 1 Luca token ≈ 100 API tokens
        const rawTokenCost = Math.ceil((inputTokens + (outputTokens * 2)) / 100);
        
        // Apply complexity multiplier
        const complexityMultiplier = this.config.complexityMultipliers[complexity];
        
        // Apply persona multiplier (Expert mode costs more)
        const persona = options?.persona || 'tutor';
        const personaMultiplier = this.config.personaMultipliers[persona] || 1.0;
        
        // Apply depth multiplier (detailed responses cost more)
        const depth = options?.depth || 'balanced';
        const depthMultiplier = this.config.depthMultipliers[depth] || 1.0;
        
        // Attachment cost
        const attachmentCost = attachmentCount * 15;
        
        // Calculate with all multipliers
        let total = Math.ceil(rawTokenCost * complexityMultiplier * personaMultiplier * depthMultiplier) + attachmentCost;
        total = Math.max(5, Math.min(total, this.config.limits.maxPerResponse));
        
        // Apply premium discount
        const userContext = user ? this.getUserContext(user) : undefined;
        let discountPercent = 0;
        if (userContext) {
            discountPercent = this.config.premiumDiscounts[userContext.tier] || 0;
        }
        const discountAmount = Math.ceil(total * (discountPercent / 100));
        total = total - discountAmount;
        
        // Unlimited users pay nothing
        if (userContext?.hasUnlimited) {
            total = 0;
        }
        
        const breakdown: { label: string; value: number }[] = [
            { label: 'Input', value: inputTokens },
            { label: 'Output (×2)', value: outputTokens * 2 },
        ];
        
        // Show complexity if it affects cost
        if (complexityMultiplier !== 1.0) {
            breakdown.push({ label: `Complexity (${complexity})`, value: Math.ceil(rawTokenCost * (complexityMultiplier - 1)) });
        }
        
        // Show persona if it affects cost (Expert mode)
        if (personaMultiplier !== 1.0) {
            const personaLabel = persona === 'professional' ? '🔬 Expert Mode' : `${persona} mode`;
            breakdown.push({ label: personaLabel, value: Math.ceil(rawTokenCost * complexityMultiplier * (personaMultiplier - 1)) });
        }
        
        // Show depth if it affects cost
        if (depthMultiplier !== 1.0) {
            const depthLabel = depth === 'concise' ? '⚡ Concise (savings)' : 
                              depth === 'detailed' ? '📝 Detailed' : 
                              depth === 'comprehensive' ? '📚 Comprehensive' : depth;
            const depthCost = Math.ceil(rawTokenCost * complexityMultiplier * personaMultiplier * (depthMultiplier - 1));
            breakdown.push({ label: depthLabel, value: depthCost });
        }
        
        if (attachmentCost > 0) {
            breakdown.push({ label: 'Attachments', value: attachmentCost });
        }
        if (discountPercent > 0) {
            breakdown.push({ label: `Discount (${discountPercent}%)`, value: -discountAmount });
        }
        
        return { tokens: total, breakdown };
    }

    /**
     * Estimate for exam generation
     */
    estimateExam(questionCount: number, difficulty: string, user?: User): EstimationResult {
        const complexityBoost = difficulty === 'very_hard' ? 2 : 1.5;
        const userContext = user ? this.getUserContext(user) : undefined;
        return this.estimate({
            message: `Generate ${questionCount} ${difficulty} exam questions`,
            operationType: 'exam',
            questionCount: Math.ceil(questionCount * complexityBoost),
        }, userContext);
    }
    
    /**
     * Get user context for estimation
     */
    getUserContext(user: User): UserContext {
        const hasUnlimited = hasUnlimitedTokens(user);
        const isMax = (user as any).isMax || (user as any).isMaxTier || user.accountTier === 'max';
        const isPremium = user.isPremium && !isMax;
        
        let tier: 'standard' | 'premium' | 'max' = 'standard';
        if (isMax) tier = 'max';
        else if (isPremium) tier = 'premium';
        
        // Get daily limit based on tier (v2.0 rebalanced economy)
        let dailyLimit = 5000; // Standard (~100 messages)
        if (isMax) dailyLimit = 200000; // MAX (~4000 messages)
        else if (isPremium) dailyLimit = 50000; // Premium (~1000 messages)
        
        return {
            tier,
            hasUnlimited,
            currentUsage: user.dailyTokenUsage || 0,
            dailyLimit,
        };
    }
    
    /**
     * Check if confirmation should be shown
     */
    shouldShowConfirmation(estimate: EstimationResult): boolean {
        return estimate.exceedsConfirmation || estimate.warnings.length > 0;
    }
    
    /**
     * Format tokens for display
     */
    formatTokens(tokens: number): string {
        if (tokens >= 1000) {
            return `${(tokens / 1000).toFixed(1)}K`;
        }
        return tokens.toString();
    }
    
    /**
     * Export config for backup
     */
    exportConfig(): string {
        return JSON.stringify(this.config, null, 2);
    }
    
    /**
     * Import config from backup
     */
    importConfig(json: string): boolean {
        try {
            const parsed = JSON.parse(json);
            this.updateConfig(parsed);
            return true;
        } catch (error) {
            console.error('[TokenEstimation] Failed to import config:', error);
            return false;
        }
    }
}

// ==================== Singleton Export ====================

export const tokenEstimationService = new TokenEstimationService();

// ==================== Convenience Exports ====================

export const getTokenEstimationConfig = () => tokenEstimationService.getConfig();
export const updateTokenEstimationConfig = (config: Partial<TokenEstimationConfig>) => tokenEstimationService.updateConfig(config);
export const resetTokenEstimationConfig = () => tokenEstimationService.resetToDefaults();
export const estimateTokens = (input: EstimationInput, userContext?: UserContext) => tokenEstimationService.estimate(input, userContext);
export const estimateChatTokens = (message: string, attachments?: Attachment[], user?: User) => tokenEstimationService.estimateChat(message, attachments || [], user);
export const estimateQuizTokens = (questionCount: number, difficulty: string, user?: User) => tokenEstimationService.estimateQuiz(questionCount, difficulty, user);
export const estimateExamTokens = (questionCount: number, difficulty: string, user?: User) => tokenEstimationService.estimateExam(questionCount, difficulty, user);
export const calculateTokensFromResponse = (
    inputChars: number,
    outputChars: number,
    complexity: ComplexityLevel,
    attachmentCount: number,
    user?: User,
    options?: {
        persona?: 'standard' | 'tutor' | 'professional';
        depth?: 'concise' | 'balanced' | 'detailed' | 'comprehensive';
    }
) => tokenEstimationService.calculateFromResponse(inputChars, outputChars, complexity, attachmentCount, user, options);
export const getUserTokenContext = (user: User) => tokenEstimationService.getUserContext(user);
export const shouldShowTokenConfirmation = (estimate: EstimationResult) => tokenEstimationService.shouldShowConfirmation(estimate);
export const formatTokenDisplay = (tokens: number) => tokenEstimationService.formatTokens(tokens);

export default tokenEstimationService;

